import { App as AntApp, ConfigProvider } from 'antd';
import enGB from 'antd/locale/en_GB';
import frCA from 'antd/locale/fr_CA';
import dayjs from 'dayjs';
import 'dayjs/locale/fr-ca';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { DocumentTitle } from './a11y/useDocumentTitle';
import { AdvisorApplicationPage } from './advisor/AdvisorApplicationPage';
import { AdvisorEntryPage } from './advisor/AdvisorEntryPage';
import { WorkflowReview } from './approver/WorkflowReview';
import { AuthGuard } from './auth/AuthGuard';
import { DevLoginPage } from './auth/DevLoginPage';
import { LoggedOutPage } from './auth/LoggedOutPage';
import { LogoutPage } from './auth/LogoutPage';
import { RequireDevLogin } from './auth/RequireDevLogin';
import { RequireRole, defaultRouteForRole } from './auth/RequireRole';
import { useRoles } from './auth/useRoles';
import { AppShell } from './components/layout/AppShell';
import { MessageBridge } from './components/MessageBridge';
import { CustomElementEditorPage } from './originator/customElements/CustomElementEditorPage';
import { CustomElementSelectorPage } from './originator/customElements/CustomElementSelectorPage';
import { DynamicFormBuilderPage } from './originator/form-builder/DynamicFormBuilderPage';
import { PdfFieldMappingPage } from './originator/PdfFieldMappingPage';
import { PreviewPage } from './originator/PreviewPage';
import { SignaturePlacementPage } from './originator/SignaturePlacementPage';
import { StepConfigPage } from './originator/StepConfigPage';
import { VersionDetailsPage } from './originator/VersionDetailsPage';
import { VersionHistoryPage } from './originator/VersionHistoryPage';
import { WorkflowDetailsPage } from './originator/WorkflowDetailsPage';
import { WorkflowEditorSession } from './originator/WorkflowEditorSession';
import { AdvisorDashboard } from './pages/AdvisorDashboard';
import { ApproverDashboard } from './pages/ApproverDashboard';
import { ArchivePage } from './pages/ArchivePage';
import { AuditHistoryPage } from './pages/AuditHistoryPage';
import { AuthErrorPage } from './pages/AuthErrorPage';
import { HealthPage } from './pages/HealthPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { OriginatorDashboard } from './pages/OriginatorDashboard';
import { PublicWorkflowPage } from './public/PublicWorkflowPage';
import { antTheme } from './theme/theme';

/**
 * Redirects an authenticated user to their role's default dashboard. Query
 * string is preserved on every hop — an advisor arriving here from ISAM
 * login (bmo-workflow-platform-spec.md:35) needs `applicationType` /
 * `applicationId` / `mode` / `language` to survive the redirect. When an
 * advisor carries `applicationType`, route to the resolver at /advisor/entry
 * instead of the plain dashboard so they land directly on their workflow form
 * rather than the picker. `applicationId` is deliberately NOT part of this
 * check: it is nullable by contract (ISAM sends it empty when the advisor has
 * no existing application, and BuildContextRedirectUrl drops empty values), and
 * AdvisorEntryPage handles its absence by minting a fresh application. Gating on
 * it here sent those advisors to the picker with their applicationType silently
 * discarded. AdvisorEntryPage still falls back to the picker on its own when
 * applicationType can't be resolved.
 */
export function RoleHome() {
  const { role, hasDualRole } = useRoles();
  const location = useLocation();
  if (hasDualRole) return <Navigate to="/auth/error?cause=dual-role" replace />;
  if (!role) return <Navigate to="/auth/error?cause=no-role" replace />;
  const params = new URLSearchParams(location.search);
  const target =
    role === 'Advisor' && params.get('applicationType')
      ? '/advisor/entry'
      : defaultRouteForRole(role);
  return <Navigate to={`${target}${location.search}`} replace />;
}

export function App() {
  const { i18n } = useTranslation();
  const antLocale = i18n.language.startsWith('fr') ? frCA : enGB;
  // The calendar's month and weekday names come from dayjs, not from the AntD locale — without this a
  // French page showed "Oct 2010 / Su Mo Tu" under a French "Aujourd'hui" (QA RIF TC-07-12). English
  // stays on dayjs's default, so the English calendar is unchanged.
  dayjs.locale(i18n.language.startsWith('fr') ? 'fr-ca' : 'en');

  return (
    <ConfigProvider
      locale={antLocale}
      theme={antTheme}
      // Render the required marker as a red asterisk *after* the label.
      form={{
        requiredMark: (label, { required }) => (
          <>
            {label}
            {required && <span className="bmo-req">*</span>}
          </>
        ),
      }}
    >
      <AntApp>
        <MessageBridge />
        {/* Mounted inside the Router (needs useLocation) but outside <Routes>,
            so one hook covers every route rather than 30 pages each having to
            remember (WCAG 2.4.2). */}
        <DocumentTitle />
        <Routes>
          {/* Public, no session required */}
          <Route element={<AppShell />}>
            <Route path="/health" element={<HealthPage />} />
            <Route path="/auth/logout" element={<LogoutPage />} />
            <Route path="/auth/logged-out" element={<LoggedOutPage />} />
            <Route path="/auth/dev-login" element={<DevLoginPage />} />
            <Route path="/auth/error" element={<AuthErrorPage />} />

            {/* The PUBLIC customer runtime (spec §2 "Public user", §12).
                Deliberately in THIS group and not the protected one: a customer
                has no session and must never be bounced to a BMO staff login.
                `/new` only, and there will never be an id-scoped variant —
                nothing about a public application is persisted anywhere, so
                there is no such thing as resuming one.
                This path is PUBLISHED: `GET /api/v1/public/workflows` hands
                every row out as `customerUrl`, built by the server's
                `PublicRuntimeOptions.CustomerUrlFor`. Renaming the route here
                breaks links other parties have already put in front of
                customers — change both sides, and treat it as a contract. */}
            <Route path="/ext/workflows/:workflowId/new" element={<PublicWorkflowPage />} />
            {/* Catch-all SCOPED to /ext. Without it a typo'd customer link falls
                through to the `*` route below, which lives INSIDE AuthGuard —
                so a mistyped URL would redirect a member of the public to the
                staff login. `/ext/*` outranks `*` in react-router's ranking, so
                the global catch-all is left exactly as it was and authenticated
                404s are unaffected. NotFoundPage is reused as-is: its copy is
                audience-neutral and it renders no links out. */}
            <Route path="/ext/*" element={<NotFoundPage />} />
          </Route>

          {/* Protected — auto-redirects to the appropriate BFF login when not signed in */}
          <Route
            element={
              <AuthGuard>
                <AppShell />
              </AuthGuard>
            }
          >
            <Route path="/" element={<RoleHome />} />

            {/* Approver-gated routes. `<RequireRole role="Approver"/>` renders
                <Outlet/> when the role check passes, else redirects. */}
            <Route element={<RequireRole role="Approver" />}>
              <Route path="/approver/dashboard" element={<ApproverDashboard />} />
              <Route path="/approver/audit-history" element={<AuditHistoryPage />} />
              <Route path="/approver/archive" element={<ArchivePage />} />
              <Route path="/approver/workflows/:id" element={<WorkflowReview />} />
              <Route path="/approver/workflows/:id/versions" element={<VersionHistoryPage />} />
              <Route path="/approver/workflows/:id/versions/:v" element={<VersionDetailsPage />} />
              <Route
                path="/approver/workflows/:id/steps/:stepId/config"
                element={<StepConfigPage />}
              />
              {/* Approver-side review preview — same component as the Originator
                  path. Lets the reviewer click "Preview" on any step row in the
                  review screen and land on that step's runtime preview. */}
              <Route path="/approver/workflows/:id/preview" element={<PreviewPage />} />
            </Route>

            {/* Originator-gated routes. */}
            <Route element={<RequireRole role="Originator" />}>
              <Route path="/originator/dashboard" element={<OriginatorDashboard />} />
              <Route path="/originator/audit-history" element={<AuditHistoryPage />} />
              <Route path="/originator/archive" element={<ArchivePage />} />
              <Route path="/originator/custom-elements" element={<CustomElementSelectorPage />} />
              <Route
                path="/originator/custom-elements/:id/edit"
                element={<CustomElementEditorPage />}
              />
              {/* One editing SESSION per workflow. The layout element owns the editor lock
                  and stays mounted across every page below it, so moving between them no
                  longer releases and re-acquires (which left the workflow briefly unlocked
                  and let a colleague slip in). The lock is released on the way out — to the
                  dashboard, or to a route outside this group.

                  The two /versions routes stay OUTSIDE on purpose: they are read-only
                  history views, and stepping into them is leaving the edit.

                  Note preview and steps/:stepId/config are inside but never ACQUIRE — see
                  useIsHoldOnlyRoute in WorkflowEditorSession. They are reachable from the
                  dashboard and Archive page, where taking a lock would hold a colleague out
                  of a Draft nobody is editing. */}
              <Route path="/originator/workflows/:id" element={<WorkflowEditorSession />}>
                <Route index element={<WorkflowDetailsPage />} />
                <Route path="steps/:stepId/edit" element={<DynamicFormBuilderPage />} />
                <Route path="steps/:stepId/config" element={<StepConfigPage />} />
                <Route path="pdf-mapping" element={<PdfFieldMappingPage />} />
                <Route path="signature-placement" element={<SignaturePlacementPage />} />
                <Route path="preview" element={<PreviewPage />} />
              </Route>
              <Route path="/originator/workflows/:id/versions" element={<VersionHistoryPage />} />
              <Route
                path="/originator/workflows/:id/versions/:v"
                element={<VersionDetailsPage />}
              />
            </Route>

            {/* Advisor-gated routes — pretend-advisor runtime for SFA-3.
                See docs/pretend-advisor.md for the swap-point index.

                BOTH application routes render the same page, deliberately.
                `/new` is an application that has no id YET: BMO mints one on
                the advisor's first save, and the page swaps the URL to the
                ID-scoped form in place (history.replaceState, no remount) so
                nothing typed is lost. Landing on `/new` calls no API at all —
                an advisor who opens the wrong workflow and backs out must not
                create a record in BMO's database.

                Past that first save each tab is isolated by its own ID URL,
                which is what INFLOWS-3 AC05/AC06 rest on.

                `/advisor/dashboard` is further gated by `RequireDevLogin`:
                spec §11 says advisors have no dashboard, so a real (non-dev-
                login) advisor session hitting it is redirected to
                /auth/error?cause=advisor-entry-required instead. */}
            <Route element={<RequireRole role="Advisor" />}>
              <Route element={<RequireDevLogin />}>
                <Route path="/advisor/dashboard" element={<AdvisorDashboard />} />
              </Route>
              <Route path="/advisor/entry" element={<AdvisorEntryPage />} />
              <Route
                path="/advisor/workflows/:templateId/applications/new"
                element={<AdvisorApplicationPage />}
              />
              <Route
                path="/advisor/workflows/:templateId/applications/:applicationId"
                element={<AdvisorApplicationPage />}
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </AntApp>
    </ConfigProvider>
  );
}
